/**
 * Visibility Model — Phase 4, Module 4 (AD-013).
 *
 * Two-level visibility system:
 *   Level 1 — Global defaults stored in `settings/portalVisibility`.
 *   Level 2 — Per-project overrides stored in `projects/{pid}.visibilityOverrides`.
 *
 * `canSee(role, section, globalMatrix, projectOverrides?)` is the single gate.
 *
 * Pure — no Firebase imports. Unit-testable in isolation.
 *
 * Owner-locked 2026-06-18.
 */

// ─── Role + Section vocab ─────────────────────────────────────────────────

export type PortalRole =
  | 'primary_client'
  | 'co_client'
  | 'family'
  | 'consultant'
  | 'partner'
  | 'staff'
  | 'admin';

export type PortalSection =
  | 'project_status'
  | 'floor_plan_grid'
  | 'concerns_goals'
  | 'auo_observations'
  | 'delivered_report'
  | 'payment_details'
  | 'internal_chat'
  | 'project_files';

export type VisibilityMatrix = Record<PortalRole, Record<PortalSection, boolean>>;

/**
 * Sparse per-project overrides. The shape is `role → section → boolean`.
 * Only the cells that differ from the global matrix are stored.
 */
export type ProjectVisibilityOverrides = Partial<
  Record<PortalRole, Partial<Record<PortalSection, boolean>>>
>;

// ─── Canonical role + section lists ──────────────────────────────────────

export const PORTAL_ROLES: PortalRole[] = [
  'primary_client',
  'co_client',
  'family',
  'consultant',
  'partner',
  'staff',
  'admin',
];

export const PORTAL_SECTIONS: PortalSection[] = [
  'project_status',
  'floor_plan_grid',
  'concerns_goals',
  'auo_observations',
  'delivered_report',
  'payment_details',
  'internal_chat',
  'project_files',
];

// ─── Approved defaults (owner-locked 2026-06-18) ─────────────────────────
//
// | Role           | ps | fp | cg | ao | dr | pd | ic | pf |
// |----------------|----|----|----|----|----|----|----|----|
// | primary_client | T  | F  | T  | F  | T  | T  | F  | T  |
// | co_client      | T  | F  | T  | F  | T  | T  | F  | F  |
// | family         | F  | F  | F  | F  | F  | F  | F  | F  |
// | consultant     | T  | T  | T  | T  | F  | F  | F  | T  |
// | partner        | T  | F  | F  | F  | F  | T  | F  | F  |
// | staff          | T  | T  | T  | T  | T  | T  | T  | T  |
// | admin          | T  | T  | T  | T  | T  | T  | T  | T  |
//
// ps=project_status, fp=floor_plan_grid, cg=concerns_goals,
// ao=auo_observations, dr=delivered_report, pd=payment_details,
// ic=internal_chat, pf=project_files

function makeRow(
  ps: boolean, fp: boolean, cg: boolean, ao: boolean,
  dr: boolean, pd: boolean, ic: boolean, pf: boolean,
): Record<PortalSection, boolean> {
  return {
    project_status: ps,
    floor_plan_grid: fp,
    concerns_goals: cg,
    auo_observations: ao,
    delivered_report: dr,
    payment_details: pd,
    internal_chat: ic,
    project_files: pf,
  };
}

export const APPROVED_DEFAULTS: VisibilityMatrix = {
  primary_client: makeRow(true,  false, true,  false, true,  true,  false, true),
  co_client:      makeRow(true,  false, true,  false, true,  true,  false, false),
  family:         makeRow(false, false, false, false, false, false, false, false),
  consultant:     makeRow(true,  true,  true,  true,  false, false, false, true),
  partner:        makeRow(true,  false, false, false, false, true,  false, false),
  staff:          makeRow(true,  true,  true,  true,  true,  true,  true,  true),
  admin:          makeRow(true,  true,  true,  true,  true,  true,  true,  true),
};

// ─── canSee — the single visibility gate ─────────────────────────────────

/**
 * PURE — determine if a given role may see a given section.
 *
 * Priority:
 *   1. `admin` always returns `true` (hard rule).
 *   2. `internal_chat` is always `false` for any non-staff / non-admin role
 *      (hard guard — cannot be overridden even by a project-level override).
 *   3. `projectOverrides[role][section]` — if explicitly set, that value wins.
 *   4. `globalMatrix[role][section]` — fallback to the global default.
 *   5. When a role or section is not in the matrix at all → `false`.
 *
 * @param role            — the viewer's portal role
 * @param section         — the section being gated
 * @param globalMatrix    — the global defaults (from `settings/portalVisibility`)
 * @param projectOverrides — optional per-project sparse overrides
 */
export function canSee(
  role: PortalRole,
  section: PortalSection,
  globalMatrix: VisibilityMatrix,
  projectOverrides?: ProjectVisibilityOverrides,
): boolean {
  // Hard rule 1: admin always sees everything.
  if (role === 'admin') return true;

  // Hard rule 2: internal chat is hidden from all non-staff roles.
  // (admin is already handled above — it returned true)
  if (section === 'internal_chat' && role !== 'staff') return false;

  // Project-level override wins (when explicitly set).
  const projRoleOverrides = projectOverrides?.[role];
  if (projRoleOverrides != null && section in projRoleOverrides) {
    // projRoleOverrides[section] may be undefined if the key is not set — use `in`
    // guard above, then access the value.
    const override = (projRoleOverrides as Record<string, boolean | undefined>)[section];
    if (typeof override === 'boolean') return override;
  }

  // Global matrix fallback.
  return globalMatrix?.[role]?.[section] ?? false;
}
